import { MDCSnackbar } from '@material/snackbar';
import '@material/snackbar/dist/mdc.snackbar.css';
import * as Sentry from '@sentry/browser';

/**
 * Renders whatever was thrown at us as a single human-readable line.
 * Used for both the snackbar label and the Sentry report, so the two always
 * agree on what went wrong.
 */
function describe(message: any): string {
  if (message instanceof Error) {
    return message.message;
  }

  if (message instanceof Event) {
    // Worst case the following property access will return undefined
    const element = message.target as HTMLScriptElement;

    return `Error event received from ${element?.localName} tag. ${element?.src}`;
  }

  if (typeof message === 'string') {
    return message;
  }

  if (typeof message?.error === 'string') {
    return message.error;
  }

  try {
    return JSON.stringify(message);
  } catch {
    // Circular structures, and anything else JSON refuses to walk.
    return String(message);
  }
}

/**
 *
 * @param message
 * @param buttonText
 * @param unexpectedError
 */
export default function createCallSnackbar(snackbarEl: HTMLDivElement | null) {
  function doCallSnackbar(
    message: any,
    // buttonText: string = 'OK',
    unexpectedError = true,
  ) {
    const description = describe(message);

    if (snackbarEl && message) {
      const snackbar = new MDCSnackbar(snackbarEl);
      snackbar.listen('MDCSnackbar:closed', handleClosed);
      // snackbar.actionButtonText = buttonText;

      snackbar.labelText = description;

      snackbar.open();
    }

    if (unexpectedError) {
      console.error(message);

      // This used to be captureException(unexpectedError), which reported the
      // boolean flag instead of the error - every event reached Sentry as
      // `true`, carrying no information at all.
      if (message instanceof Error) {
        Sentry.captureException(message);
      } else {
        // captureException on a non-Error yields a bare "Non-Error exception
        // captured" with no detail, so report these as messages instead and
        // attach the raw value for context.
        Sentry.withScope((scope) => {
          scope.setExtra('raw', message);
          Sentry.captureMessage(description, Sentry.Severity.Error);
        });
      }
    }
  }

  const queue = new Array<Function>();

  function handleClosed() {
    queue.shift();
    if (queue.length > 0) {
      queue[0]();
    }
  }

  return function callSnackbar(message: any, unexpectedError = true) {
    queue.push(() => doCallSnackbar(message, unexpectedError));
    if (queue.length === 1) {
      queue[0]();
    }
  };
}
